import React, { useState, useRef, useEffect } from 'react';
import { Plus, Download, Search, Filter, MoreHorizontal, X, Eye, Trash, FileUp, FileDown, Edit, Award, Save, AlertCircle } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import QRCode from 'qrcode';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { editCertificate, fetchCertificates, importCertificatesCsv, issueCertificate, deleteCertificate, downloadSampleCertificateFile } from "../../services/dashboardService";
import { toast } from 'react-toastify';
import api from '@/services/api';

export const Certificates: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [viewCert, setViewCert] = useState<any | null>(null);
    const [actionMenuOpen, setActionMenuOpen] = useState<number | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilters, setShowFilters] = useState(false);
    const [itemsPerPage] = useState(20);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [selectAll, setSelectAll] = useState(false);
    const [certificates, setCertificates] = useState<any[]>([]);
    const [totalCertificates, setTotalCertificates] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [calibrated, setCalibrated] = useState(false);
    const [loadingList, setLoadingList] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    
    // Import state
    const [importing, setImporting] = useState(false);
    
    // Edit State
    const [editingCertId, setEditingCertId] = useState<number | null>(null);
    const [editingCertData, setEditingCertData] = useState<any | null>(null);
    const [initialIssuanceDate, setInitialIssuanceDate] = useState('');

    // Delete Confirmation Modal States
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [certToDelete, setCertToDelete] = useState<any | null>(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');

    // Filters
    const [statusFilter, setStatusFilter] = useState('All');
    const [courseFilter, setCourseFilter] = useState('All');
    const [dateFilter, setDateFilter] = useState('');

    const fileInputRef = useRef<HTMLInputElement>(null);
    const certificateRef = useRef<HTMLDivElement>(null);

    const CERT_BG_URL = '/verify/certificate-bg.jpg';

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        courseOfStudy: '',
        courseSummary: '',
        yearOfStudy: '2025',
        issuanceDate: '',
        certificateNumber: ''
    });

    // ===================================
    // FIXED: Download sample file function
    // ===================================
    const handleDownloadSample = async (format: 'csv' | 'xlsx' = 'csv') => {
        try {
            await downloadSampleCertificateFile(format);
            toast.success(`Sample ${format.toUpperCase()} file downloaded successfully`);
        } catch (error: any) {
            toast.error(error?.message || "Failed to download sample file");
        }
    };

    const buildQrPayload = (certLike: any) => {
        const certificate_number = String(certLike?.certId || certLike?.certificateId || '').trim();
        const verify_url = `https://speedlinktraining.com/verify/verify/${certificate_number}`;
        return verify_url;
    };

    const verifyLink = (certLike: any) => {
        const certificate_number = String(certLike?.certId || certLike?.certificateId || '').trim();
        return `https://speedlinktraining.com/verify/verify/${certificate_number}`;
    }

    const formatDate = (dateString: string): string => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        const day = date.getDate();
        const month = date.toLocaleString('default', { month: 'long' });
        const year = date.getFullYear();
        const getOrdinalSuffix = (d: number) => {
            if (d > 3 && d < 21) return 'th';
            switch (d % 10) {
                case 1:  return 'st';
                case 2:  return 'nd';
                case 3:  return 'rd';
                default: return 'th';
            }
        };
        return `${day}${getOrdinalSuffix(day)} of ${month} ${year}`;
    };

    const filteredCertificates = certificates.filter((cert: any) => {
        const q = (searchQuery || '').toLowerCase();
        const name = String(cert?.name || '').toLowerCase();
        const id = String(cert?.certId || '').toLowerCase();
        const course = String(cert?.course || '').toLowerCase();
        const matchesSearch = name.includes(q) || id.includes(q) || course.includes(q);
        const statusVal = String(cert?.status || '');
        const matchesStatus = statusFilter === 'All' || statusVal === statusFilter;
        const matchesCourse = courseFilter === 'All' || cert?.course === courseFilter;
        const dateStr = String(cert?.date || '');
        const matchesDate = dateFilter === '' || dateStr.slice(0, 10) === dateFilter;
        return matchesSearch && matchesStatus && matchesCourse && matchesDate;
    });

    const indexOfLastItem = currentPage * itemsPerPage; 
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredCertificates.slice(indexOfFirstItem, indexOfLastItem);
    const totalPagesComputed = Math.max(1, Math.ceil(filteredCertificates.length / itemsPerPage || 1));

    useEffect(() => {
        const loadCertificates = async () => {
            try {
                setLoadingList(true);
                if (certificates.length === 0) {
                    try {
                        const cached = localStorage.getItem('certificates_cache');
                        console.log('Loaded cached certificates:', cached);
                        if (cached) {
                            const items = JSON.parse(cached) || [];
                            const mapped = items.map((c: any) => ({
                                id: c.id,
                                name: c.student_name,
                                course: c.course_name,
                                certId: c.verification_code,
                                date: c.issued_at,
                                verifyUrl: c.verification_url || c.verify_url || undefined,
                                qrImageUrl: c.qr_url || c.qr_code_url || undefined,
                                courseSummary: c.course_summary || '',
                                status: (c.status || c.verification_status || c.state || '').toString(),
                            }));
                            if (mapped.length > 0) setCertificates(mapped);
                        }
                    } catch {}
                }
                const data = await fetchCertificates();
                const mapped = (data.certificates || []).map((c: any) => ({
                    id: c.id,
                    name: c.student_name,
                    course: c.course_name,
                    certId: c.verification_code,
                    date: c.issued_at,
                    verifyUrl: c.verification_url || c.verify_url || undefined,
                    qrImageUrl: c.qr_url || c.qr_code_url || undefined,
                    courseSummary: c.course_summary || '',
                    status: (c.status || c.verification_status || c.state || '').toString(),
                }));
                setCertificates(mapped);
                setTotalCertificates(mapped.length);
                try { localStorage.setItem('certificates_cache', JSON.stringify(data.certificates)); } catch {}
            } catch (err) {
                console.error(err);
                setTotalPages(totalPages || 10);
            }
            finally {
                setLoadingList(false);
            }
        };
        loadCertificates();
    }, []);

    const getFitSize = (t: string, base: number, min: number, max: number) => {
        const W = 606;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        let s = base;
        if (!ctx) return Math.min(Math.max(s, min), max);
        while (s > min) {
            ctx.font = `${s}px 'Times New Roman', Times, serif`;
            if (ctx.measureText(t || '').width <= W) break;
            s -= 1;
        }
        if (s < min) s = min;
        if (s > max) s = max;
        return s;
    };
    
    const getFitSizeNarrow = (t: string, base: number, min: number, max: number) => {
        const W = 260;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        let s = base;
        if (!ctx) return Math.min(Math.max(s, min), max);
        while (s > min) {
            ctx.font = `${s}px 'Courier New', monospace`;
            if (ctx.measureText(t || '').width <= W) break;
            s -= 1;
        }
        if (s < min) s = min;
        if (s > max) s = max;
        return s;
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPagesComputed) {
            setCurrentPage(newPage);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveCertificate = async () => {
        console.log("=== SAVE CERTIFICATE STARTED ===");
        console.log("Current form data:", formData);
        console.log("Editing cert ID:", editingCertId);
        
        // Validation
        if (!formData.firstName || !formData.lastName || !formData.courseOfStudy) {
            toast.error("Please fill in the required fields: First Name, Last Name, and Course of Study.");
            return;
        }

        const isEditing = !!editingCertId;
        console.log("Is editing mode:", isEditing);
        
        // Prepare payload
        const payload: any = {
            first_name: formData.firstName.trim(),
            last_name: formData.lastName.trim(),
            student_name: `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim(),
            course_name: formData.courseOfStudy.trim(),
            course_summary: formData.courseSummary.trim(),
            year_of_study: formData.yearOfStudy,
        };

        // Add issuance date if provided (only send ISO yyyy-MM-dd). If the
        // provided value can't be parsed into ISO, omit the fields to avoid
        // backend parsing errors that cause 500 responses.
        if (formData.issuanceDate) {
            const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (isoDateRegex.test(formData.issuanceDate)) {
                payload.issuance_date = formData.issuanceDate;
                payload.issued_at = formData.issuanceDate;
            } else {
                const parsed = Date.parse(formData.issuanceDate);
                if (!isNaN(parsed)) {
                    const iso = new Date(parsed).toISOString().slice(0, 10);
                    payload.issuance_date = iso;
                    payload.issued_at = iso;
                } else {
                    console.warn('Omitting malformed issuanceDate to avoid server error:', formData.issuanceDate);
                }
            }
        }

        // Add certificate number if provided
        if (formData.certificateNumber && formData.certificateNumber.trim()) {
            const code = formData.certificateNumber.trim();
            payload.verification_code = code;
            payload.certificate_code = code;
        }

        // For editing: include original image_url and pdf_url from existing cert
        if (isEditing && editingCertData) {
            payload.image_url = editingCertData.image_url || "";
            payload.pdf_url = editingCertData.pdf_url || "";
            payload.is_active = editingCertData.is_active !== undefined ? editingCertData.is_active : true;
        }

        console.log("Final payload:", payload);

        try {
            if (isEditing) {
                // ===== EDITING EXISTING CERTIFICATE =====
                console.log("🟡 Calling editCertificate API...");
                
                const saved = await editCertificate(formData.certificateNumber || editingCertData?.certId, payload);
                console.log("✅ Edit API returned:", saved);

                // Update local state immediately
                setCertificates(prev => prev.map(c => {
                    if (c.id !== editingCertId) return c;
                    
                    const newName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();
                    const newCourse = formData.courseOfStudy.trim();
                    const newCertId = saved?.verification_code || formData.certificateNumber?.trim() || c.certId;
                    const newDate = saved?.issued_at || formData.issuanceDate || c.date;
                    const newSummary = saved?.course_summary || formData.courseSummary?.trim() || c.courseSummary;
                    
                    console.log("Updating certificate locally:", {
                        id: c.id,
                        oldName: c.name,
                        newName,
                        oldCourse: c.course,
                        newCourse,
                        oldCertId: c.certId,
                        newCertId,
                        oldDate: c.date,
                        newDate
                    });
                    
                    return {
                        ...c,
                        name: newName,
                        course: newCourse,
                        certId: newCertId,
                        date: newDate,
                        courseSummary: newSummary,
                        status: c.status || "Verified",
                    };
                }));

                toast.success("Certificate updated successfully!");

                // Refresh data from server in background
                setTimeout(async () => {
                    try {
                        console.log("🔄 Refreshing certificates from server...");
                        const data = await fetchCertificates();
                        const mapped = (data.certificates || []).map((c: any) => ({
                            id: c.id,
                            name: c.student_name,
                            course: c.course_name,
                            certId: c.verification_code,
                            date: c.issued_at,
                            verifyUrl: c.verification_url || c.verify_url || undefined,
                            qrImageUrl: c.qr_url || c.qr_code_url || undefined,
                            courseSummary: c.course_summary || '',
                            status: (c.status || c.verification_status || c.state || '').toString(),
                        }));
                        setCertificates(mapped);
                        setTotalCertificates(mapped.length);
                        localStorage.setItem('certificates_cache', JSON.stringify(data.certificates));
                        console.log("✅ Certificates refreshed successfully");
                    } catch (err) {
                        console.error("❌ Background refresh failed:", err);
                    }
                }, 1000);

            } else {
                // ===== CREATING NEW CERTIFICATE =====
                console.log("🟢 Calling issueCertificate API...");
                
                const saved = await issueCertificate(payload);
                console.log("✅ Create API returned:", saved);

                const created = {
                    id: saved.id,
                    name: saved.student_name,
                    course: saved.course_name,
                    certId: saved.verification_code,
                    date: saved.issued_at,
                    courseSummary: saved.course_summary || '',
                    status: "Verified",
                };
                
                setCertificates(prev => [created, ...prev]);
                toast.success("Certificate issued successfully!");
            }

            // Close modal and reset form
            console.log("🔵 Closing modal and resetting form...");
            setIsModalOpen(false);
            setEditingCertId(null);
            setEditingCertData(null);
            setInitialIssuanceDate('');
            setActionMenuOpen(null);
            setViewCert(null);
            setFormData({
                firstName: "",
                lastName: "",
                courseOfStudy: "",
                courseSummary: "",
                yearOfStudy: "2025",
                issuanceDate: "",
                certificateNumber: ""
            });
            
            console.log("=== SAVE CERTIFICATE COMPLETED ===");

        } catch (err: any) {
            console.error("❌❌❌ Error saving certificate:", err);
            console.error("Error details:", {
                message: err?.message,
                response: err?.response?.data,
                status: err?.response?.status
            });
            
            const errorMessage = err?.response?.data?.message 
                || err?.message 
                || "An error occurred while saving the certificate.";
            
            toast.error(errorMessage);
        }
    };

    const openDeleteModal = (cert: any, e: React.MouseEvent) => {
        e.stopPropagation();
        setCertToDelete(cert);
        setDeleteConfirmText('');
        setDeleteModalOpen(true);
        setActionMenuOpen(null);
    };

    const confirmAndDelete = async () => {
        if (!certToDelete || deleteConfirmText.toLowerCase() !== 'delete') return;
        try {
            await deleteCertificate(certToDelete.id);
            setCertificates(prev => prev.filter(c => c.id !== certToDelete.id));
            toast.success("Certificate deleted successfully.");
        } catch (err: any) {
            console.error("Delete error:", err);
            toast.error(err?.message || "Failed to delete certificate");
        } finally {
            setDeleteModalOpen(false);
            setCertToDelete(null);
            setDeleteConfirmText('');
        }
    };

    const generatePDF = async (cert: any) => {
        setIsGeneratingPdf(true);
        try {
            const loadImageAsDataUrl = (url: string, mime: string = 'image/png') => new Promise<string>((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                    try {
                        const c = document.createElement('canvas');
                        c.width = img.naturalWidth;
                        c.height = img.naturalHeight;
                        const ctx = c.getContext('2d');
                        if (!ctx) return reject(new Error('Canvas context unavailable'));
                        ctx.drawImage(img, 0, 0);
                        const data = c.toDataURL(mime);
                        resolve(data);
                    } catch (err) {
                        reject(err);
                    }
                };
                img.onerror = (e) => reject(e);
                img.src = url;
            });

            let bg: string;
            try {
                bg = await loadImageAsDataUrl(CERT_BG_URL, 'image/jpeg');
            } catch (e) {
                console.error('Failed to load certificate background image via image loader', e);
                try {
                    const r = await fetch(CERT_BG_URL);
                    const b = await r.blob();
                    bg = await new Promise<string>((resolve) => { const fr = new FileReader(); fr.onloadend = () => resolve(fr.result as string); fr.readAsDataURL(b); });
                } catch (e2) {
                    console.warn('Fallback fetch also failed for background image', e2);
                    if (certificateRef?.current) {
                        try {
                            const canvas = await html2canvas(certificateRef.current as HTMLElement, { scale: 2 });
                            bg = canvas.toDataURL('image/jpeg', 1.0);
                        } catch (e3) {
                            console.error('html2canvas fallback also failed', e3);
                            throw new Error('Failed to load certificate background image');
                        }
                    } else {
                        throw new Error('Failed to load certificate background image');
                    }
                }
            }
            const img = new Image();
            img.src = bg;
            await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = (e) => rej(e); });
            const { naturalWidth: w, naturalHeight: h } = img;
            const k = Math.min(w / 842, h / 595);
            const px = (n: number) => Math.round(n * k);
            const cx = (n: number) => Math.round((n * w) / 842);
            const cy = (n: number) => Math.round((n * h) / 595);

            const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [w, h] });
            pdf.addImage(bg, 'JPEG', 0, 0, w, h, undefined, 'NONE');
            pdf.setFont('times', 'bold');
            let sName = px(50);
            const maxW = cx(606);
            while (sName > px(22)) { pdf.setFontSize(sName); if (pdf.getTextWidth(cert.name || '') <= maxW) break; sName -= 1; }
            pdf.setFontSize(sName);
            pdf.setTextColor('#111827');
            pdf.text(cert.name, w / 2, cy(266), { align: 'center' });
            pdf.setFont('times', 'normal');
            let sCourse = px(30);
            pdf.setFontSize(sCourse);
            pdf.setTextColor('#1e3a8a');
            const courseText = (cert.course || '').toUpperCase();
            let courseLines = pdf.splitTextToSize(courseText, maxW);
            if (courseLines.length > 2) courseLines = courseLines.slice(0, 2);
            if (courseLines.length > 1) { sCourse = px(26); pdf.setFontSize(sCourse); }
            const baseY = cy(346);
            const gap = px(26);
            const startY = baseY - Math.round(gap * (courseLines.length - 1) / 2);
            courseLines.forEach((ln: string, i: number) => {
                pdf.text(ln, Math.round(w / 2), startY + i * gap, { align: 'center' });
            });
            pdf.setFont('times', 'bold');
            pdf.setFontSize(px(16));
            pdf.setTextColor('#111827');
            pdf.text(formatDate(cert.date), cx(360), cy(405));
            pdf.setFont('times', 'normal');
            pdf.setFontSize(px(10));
            pdf.setTextColor('#111827e9');
            pdf.text(verifyLink(cert), cx(348), cy(532));
            pdf.setFont('courier', 'normal');
            const sIdBase = cert.certId === 'SHSL/25B/CS/CEH/0034' ? px(15) : px(17);
            const sIdMin = cert.certId === 'SHSL/25B/CS/CEH/0034' ? px(9) : px(10);
            let sId = sIdBase;
            const maxIdW = cx(260);
            while (sId > sIdMin) { pdf.setFontSize(sId); if (pdf.getTextWidth(cert.certId || '') <= maxIdW) break; sId -= 1; }
            pdf.setFontSize(sId);
            pdf.setTextColor('#6b7280');
            pdf.text(cert.certId, w - cx(50), cy(90), { align: 'right' });
            const qrPayload = buildQrPayload(cert);
            let qrData: string | null = null;
            try {
                qrData = await QRCode.toDataURL(qrPayload, {
                    margin: 0,
                    errorCorrectionLevel: 'H',
                });
            } catch (e) {
                console.warn('QR generation failed, skipping QR:', e);
                qrData = null;
            }
            if (qrData) {
                try {
                    pdf.addImage(qrData, 'PNG', w - cx(450), cy(459), cx(60), cy(55));
                } catch (e) {
                    console.warn('Failed to add QR image to PDF, skipping QR:', e);
                }
            }
            pdf.save(`Certificate-${cert.certId}.pdf`);
        } catch (err) {
            console.error('PDF Gen Error', err);
            const msg = err instanceof Error ? err.message : String(err);
            toast.error(`Failed to generate PDF: ${msg}`);
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const generatePDFForCert = async (cert: any) => {
        setViewCert(cert);
        await new Promise(r => setTimeout(r, 300));
        await generatePDF(cert);
    };

    const handleToggleAll = (checked: boolean) => {
        setSelectAll(checked);
        setSelectedIds(checked ? filteredCertificates.map(c => c.id) : []);
    };

    const handleToggleOne = (id: number, checked: boolean) => {
        setSelectedIds(prev => checked ? [...prev, id] : prev.filter(x => x !== id));
    };

    const handleBulkDownload = async () => {
        const items = selectedIds.length > 0
            ? certificates.filter(c => selectedIds.includes(c.id))
            : filteredCertificates;
        for (const item of items) {
            await generatePDFForCert(item);
        }
        setViewCert(null);
    };

    const handleImportClick = () => fileInputRef.current?.click();

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const allowed = [".csv", ".xlsx", ".xls"];
        const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
        if (!allowed.includes(ext)) {
            toast.error("Please upload a CSV or Excel file.");
            e.target.value = "";
            return;
        }
        
        try {
            setImporting(true);
            toast.info("Uploading...");
            const result = await importCertificatesCsv(file);
            
            toast.success(
                <div>
                    <strong>Import Successful!</strong>
                    <div className="text-sm mt-1">
                        <div>Imported: {result.imported} certificates</div>
                        {result.errors && result.errors.length > 0 && (
                            <div className="text-yellow-300 mt-1">
                                Errors: {result.errors.length} (see console)
                            </div>
                        )}
                    </div>
                </div>,
                { autoClose: 5000 }
            );
            
            const data = await fetchCertificates();
            const mapped = (data.certificates || []).map((c: any) => ({
                id: c.id,
                name: c.student_name,
                course: c.course_name,
                certId: c.verification_code,
                date: c.issued_at,
                status: "Verified"
            }));
            setCertificates(mapped);
            setTotalCertificates(mapped.length);
            
            if (result.errors && result.errors.length > 0) {
                console.error("Import errors:", result.errors);
            }
        } catch (err: any) {
            toast.error(err?.message || "Import failed");
        } finally {
            setImporting(false);
            e.target.value = "";
        }
    };

    const handleExport = () => {
        const headers = ["Name", "Course", "Certificate ID", "Date", "Status"];
        const rows = certificates.map(c => [c.name, c.course, c.certId, c.date, c.status]);
        const csv = "data:text/csv;charset=utf-8," + headers.join(",") + "\n" + rows.map(r => `"${r.join('","')}"`).join("\n");
        const link = document.createElement("a");
        link.setAttribute("href", encodeURI(csv));
        link.setAttribute("download", "certificates.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const toggleActionMenu = (id: number) => {
        setActionMenuOpen(prev => prev === id ? null : id);
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (!(e.target as Element).closest('.action-menu-container')) {
                setActionMenuOpen(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleEditClick = (cert: any) => {
        const [first, ...last] = cert.name.split(' ');
        const normalizedDate = (() => {
            const v = cert.date;
            if (!v) return '';
            const isoRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (typeof v === 'string') {
                if (isoRegex.test(v)) return v.slice(0, 10);
                const parsed = Date.parse(v);
                if (!isNaN(parsed)) return new Date(parsed).toISOString().slice(0, 10);
                return '';
            }
            if (v instanceof Date) {
                const t = new Date(v.getTime() - v.getTimezoneOffset() * 60000);
                return t.toISOString().slice(0, 10);
            }
            return '';
        })();
        setFormData({
            firstName: first || '',
            lastName: last.join(' '),
            courseOfStudy: cert.course,
            courseSummary: cert.courseSummary || '',
            yearOfStudy: '2025',
            issuanceDate: normalizedDate,
            certificateNumber: cert.certId
        });
        setInitialIssuanceDate(normalizedDate);
        setEditingCertId(cert.id);
        setEditingCertData(cert);
        setIsModalOpen(true);
        setActionMenuOpen(null);
    };

    const handleOpenIssueModal = () => {
        setFormData({
            firstName: '', lastName: '', courseOfStudy: '', courseSummary: '',
            yearOfStudy: '2025', issuanceDate: '', certificateNumber: ''
        });
        setEditingCertId(null);
        setEditingCertData(null);
        setInitialIssuanceDate('');
        setIsModalOpen(true);
    };

    return (
        <div className="space-y-6">
            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".csv,.xlsx,.xls" className="hidden" />

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Certificates</h2>
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mt-1">Manage and verify issued certificates.</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
                        <input 
                            type="text" 
                            placeholder="Search certificates..." 
                            value={searchQuery}
                            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                            className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm w-full md:w-64 focus:ring-primary focus:border-primary bg-white dark:bg-gray-700"
                        />
                    </div>
                    <button 
                        onClick={handleImportClick} 
                        disabled={importing}
                        className="bg-white dark:bg-gray-800 border border-primary text-primary px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-50 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50"
                    >
                        <FileUp className="w-4 h-4" /> {importing ? 'Importing...' : 'Import CSV'}
                    </button>
                    <button onClick={handleOpenIssueModal} className="bg-primary hover:bg-indigo-800 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
                        <Plus className="w-4 h-4" /> Issue Certificate
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border overflow-hidden">
                <div className="p-4 border-b flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex items-center gap-2">
                        <button onClick={handleExport} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-md text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600">
                            <FileDown className="w-4 h-4" /> Export
                        </button>
                        <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-3 py-1.5 border rounded-md text-sm font-medium ${showFilters ? 'bg-primary text-white border-primary' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                            <Filter className="w-3 h-3" /> Filters
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleBulkDownload}
                            disabled={selectedIds.length === 0}
                            className={`px-3 py-1.5 border rounded-md text-xs font-semibold ${
                                selectedIds.length > 0
                                    ? 'border-primary text-white bg-primary hover:bg-indigo-900 dark:hover:bg-gray-700'
                                    : 'border-gray-300 text-gray-400 cursor-not-allowed bg-gray-100 dark:bg-gray-700'
                            }`}
                        >
                            Download Selected
                        </button>
                    </div>
                </div>

                {showFilters && (
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border-b grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} className="border rounded-md p-2 text-sm">
                            <option value="All">Status: All</option>
                            <option value="Verified">Verified</option>
                            <option value="Revoked">Revoked</option>
                        </select>
                        <select value={courseFilter} onChange={(e) => { setCourseFilter(e.target.value); setCurrentPage(1); }} className="border rounded-md p-2 text-sm">
                            <option value="All">Course: All</option>
                            <option value="Full Stack Web Dev">Full Stack Web Dev</option>
                            <option value="Data Analytics">Data Analytics</option>
                            <option value="Cybersecurity">Cybersecurity</option>
                        </select>
                        <input type="date" value={dateFilter} onChange={(e) => { setDateFilter(e.target.value); setCurrentPage(1); }} className="border rounded-md p-2 text-sm" />
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-gray-700 dark:text-gray-300">
                        <thead className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs uppercase font-bold">
                            <tr>
                                <th className="px-6 py-3"><input type="checkbox" checked={selectAll} onChange={(e) => handleToggleAll(e.target.checked)} className="rounded" /></th>
                                <th className="px-6 py-3">Name</th>
                                <th className="px-6 py-3">Course</th>
                                <th className="px-6 py-3">Certificate ID</th>
                                <th className="px-6 py-3">Date</th>
                                <th className="px-6 py-3">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {currentItems.length > 0 ? currentItems.map((cert, idx) => (
                                <tr key={`${cert?.id ?? cert?.certId ?? cert?.name ?? 'row'}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-6 py-4"><input type="checkbox" checked={selectedIds.includes(cert.id)} onChange={(e) => handleToggleOne(cert.id, e.target.checked)} className="rounded" /></td>
                                    <td className="px-6 py-4 font-semibold text-gray-900 dark:text-white">{cert.name}</td>
                                    <td className="px-6 py-4">{cert.course}</td>
                                    <td className="px-6 py-4 font-mono text-xs">{cert.certId}</td>
                                    <td className="px-6 py-4">{cert.date}</td>
                                    <td className="px-6 py-4 action-menu-container relative">
                                        <button onClick={(e) => { e.stopPropagation(); toggleActionMenu(cert.id); }} className="p-1">
                                            <MoreHorizontal className="w-5 h-5" />
                                        </button>
                                        {actionMenuOpen === cert.id && (
                                            <div className="absolute right-8 top-0 mt-2 w-32 bg-white dark:bg-gray-800 rounded-md shadow-lg border z-50">
                                                <div className="py-1">
                                                    <button onClick={() => { setViewCert(cert); setActionMenuOpen(null); }} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left font-medium">
                                                        <Eye className="w-4 h-4" /> View
                                                    </button>
                                                    <button onClick={() => handleEditClick(cert)} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left font-medium">
                                                        <Edit className="w-4 h-4" /> Edit
                                                    </button>
                                                    <button onClick={(e) => openDeleteModal(cert, e)} className="flex items-center gap-2 px-4 py-2 text-sm text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 w-full text-left font-medium">
                                                        <Trash className="w-4 h-4" /> Delete
                                                    </button> 
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan={6} className="text-center py-8 text-gray-500">No certificates found</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="p-4 border-t flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-800/50">
                    <span>
                        {filteredCertificates.length === 0
                            ? 'Showing 0-0 of 0'
                            : `Showing ${indexOfFirstItem + 1}-${Math.min(indexOfLastItem, filteredCertificates.length)} of ${filteredCertificates.length}`}
                    </span>
                    <div className="flex gap-2">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded disabled:opacity-50">Previous</button>
                        {Array.from({ length: totalPagesComputed }, (_, i) => i + 1).map(p => (
                            <button key={p} onClick={() => handlePageChange(p)} className={`px-3 py-1 rounded ${currentPage === p ? 'bg-primary text-white' : 'border'}`}>{p}</button>
                        ))}
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPagesComputed} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
                    </div>
                </div>
            </div>

            {/* DELETE CONFIRMATION MODAL */}
            {deleteModalOpen && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6 border relative">
                        <button
                            onClick={() => {
                                setDeleteModalOpen(false);
                                setCertToDelete(null);
                                setDeleteConfirmText('');
                            }}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        <div className="flex items-center gap-3 text-red-600 mb-4">
                            <AlertCircle className="w-8 h-8" />
                            <h3 className="text-xl font-bold">Delete Certificate?</h3>
                        </div>

                        <p className="text-gray-700 dark:text-gray-300 mb-6">
                            Are you sure you want to delete the certificate for <strong>{certToDelete?.name}</strong>?<br />
                            <span className="font-bold text-red-600">This action cannot be undone.</span>
                        </p>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Type <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">delete</span> to confirm
                            </label>
                            <input
                                type="text"
                                value={deleteConfirmText}
                                onChange={(e) => setDeleteConfirmText(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="delete"
                                autoFocus
                            />
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setDeleteModalOpen(false);
                                    setCertToDelete(null);
                                    setDeleteConfirmText('');
                                }}
                                className="px-5 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmAndDelete}
                                disabled={deleteConfirmText.toLowerCase() !== 'delete'}
                                className="px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                            >
                                <Trash className="w-4 h-4" /> Proceed & Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Certificate Modal */}
            {viewCert && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-5xl shadow-2xl p-6 border max-h-[95vh] overflow-auto">
                        <div className="flex justify-between items-center mb-4 pb-2 border-b">
                            <h3 className="text-lg font-bold flex items-center gap-2"><Award className="w-5 h-5 text-yellow-600" /> Certificate Preview</h3>
                            <div className="flex gap-3">
                                <button onClick={() => generatePDF(viewCert)} disabled={isGeneratingPdf} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-800 flex items-center gap-2 disabled:opacity-50">
                                    {isGeneratingPdf ? 'Generating...' : <><Download className="w-4 h-4" /> Download PDF</>}
                                </button>
                                <button onClick={() => setViewCert(null)}><X className="w-6 h-6" /></button>
                            </div>
                        </div>
                        <div className="flex-grow overflow-auto bg-gray-200 dark:bg-gray-900 p-8 rounded-lg flex items-center justify-center">
                            <div ref={certificateRef} className="w-[842px] h-[595px] relative shadow-2xl bg-white" style={{ backgroundImage: `url(${CERT_BG_URL})`, backgroundSize: '100% 100%' }}>
                                <div className="relative z-10 w-full h-full text-center px-0">
                                    <h2 className="font-serif font-bold text-gray-900 capitalize absolute left-1/2 -translate-x-1/2" style={{ top: '235px', fontSize: `${getFitSize(viewCert.name, 32, 20, 30)}px` }}>
                                        {viewCert.name}
                                    </h2>
                                    <h3 className="font-medium text-indigo-900 uppercase absolute left-1/2 -translate-x-1/2 max-w-[92%] text-center whitespace-normal break-words" style={{ top: '305px', fontSize: `${getFitSize(viewCert.course, 26, 16, 20)}px` }}>
                                        {viewCert.course}
                                    </h3>
                                    <div className="absolute" style={{ top: '395px', left: '340px', fontSize: '14px' }}>
                                        <p className=" text-gray-800 font-bold">{formatDate(viewCert.date)}</p>
                                    </div>
                                    <div className="absolute" style={{ top: '522px', left: '349px', fontSize: '10px' }}>
                                        <p className="text-gray-800 italic">{verifyLink(viewCert)}</p>
                                    </div>
                                    <div className="absolute" style={{ top: '73px', right: '49px' }}>
                                        <span className="font-mono text-gray-800" style={{ fontSize: `${viewCert.certId === 'SHSL/25B/CS/CEH/0034' ? getFitSizeNarrow(viewCert.certId, 12, 9, 14) : getFitSizeNarrow(viewCert.certId, 14, 10, 16)}px` }}>{viewCert.certId}</span>
                                    </div>
                                    <div className="absolute" style={{ top: '435px', right: '383px', width: '85px', height: '60px' }}>
                                        <QRCodeCanvas
                                            value={buildQrPayload(viewCert)}
                                            size={65}
                                            level="H"
                                            includeMargin={false}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Issue/Edit Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto border">
                        <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 z-10">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">{editingCertId ? 'Update Certificate' : 'Issue Certificate'}</h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-6 space-y-8">
                            {!editingCertId && (
                                <div className="bg-gray-50 dark:bg-gray-700/50 p-6 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
                                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-2">To issue bulk certificates you can import the database below</p>
                                    {/* FIXED: Download sample link now uses the handleDownloadSample function */}
                                    <button 
                                        onClick={() => handleDownloadSample('csv')}
                                        className="text-xs text-primary hover:underline mb-4 block font-semibold"
                                    >
                                        Download Sample CSV File
                                    </button>
                                    <button 
                                        onClick={handleImportClick} 
                                        disabled={importing}
                                        className="flex items-center gap-2 px-4 py-2 border border-primary text-primary dark:text-indigo-400 bg-white dark:bg-gray-800 rounded-md text-sm font-semibold hover:bg-blue-50 dark:hover:bg-gray-700 shadow-sm disabled:opacity-50"
                                    >
                                        <FileUp className="w-4 h-4" /> {importing ? 'Importing...' : 'Import CSV File'}
                                    </button>
                                </div>
                            )}

                            <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Input Student Details</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {['First Name', 'Last Name', 'Course of Study', 'Course Summary', 'Year of Study', 'Issuance Date', 'Certificate Number'].map(label => {
                                    const name = label.toLowerCase().replace(/\s/g, '').replace('ofstudy', 'OfStudy').replace('firstname', 'firstName').replace('lastname', 'lastName').replace('coursesummary', 'courseSummary').replace('yearofstudy', 'yearOfStudy').replace('issuancedate', 'issuanceDate').replace('certificatenumber', 'certificateNumber');
                                    return (
                                        <div key={label}>
                                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">{label} {label !== 'Certificate Number' && '*'}</label>
                                            <input 
                                                type={label.includes('Date') ? 'date' : 'text'}
                                                name={name} 
                                                value={(formData as any)[name]} 
                                                onChange={handleInputChange} 
                                                required={label !== 'Certificate Number' && !editingCertId}
                                                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 focus:ring-primary focus:border-primary text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700" 
                                                placeholder={`Enter ${label}`} 
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="mt-8">
                                <button onClick={handleSaveCertificate} className="w-fit bg-primary text-white px-8 py-3 rounded-lg text-sm font-bold hover:bg-indigo-800 shadow-md flex items-center gap-2">
                                    {editingCertId ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                    {editingCertId ? 'Update Certificate' : 'Issue Certificate'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Certificates;












// import React, { useState, useRef, useEffect } from 'react';
// import { Plus, Download, Search, Filter, MoreHorizontal, X, Eye, Trash, FileUp, FileDown, Edit, Award, Save, AlertCircle } from 'lucide-react';
// import { QRCodeCanvas } from 'qrcode.react';
// import QRCode from 'qrcode';
// import { jsPDF } from 'jspdf';
// import html2canvas from 'html2canvas';
// import { editCertificate, fetchCertificates, importCertificatesCsv, issueCertificate, deleteCertificate } from "../../services/dashboardService";
// import { toast } from 'react-toastify';
// import api from '@/services/api';

// export const Certificates: React.FC = () => {
//     const [isModalOpen, setIsModalOpen] = useState(false);
//     const [viewCert, setViewCert] = useState<any | null>(null);
//     const [actionMenuOpen, setActionMenuOpen] = useState<number | null>(null);
//     const [searchQuery, setSearchQuery] = useState('');
//     const [currentPage, setCurrentPage] = useState(1);
//     const [showFilters, setShowFilters] = useState(false);
//     const [itemsPerPage] = useState(20);
//     const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
//     const [selectedIds, setSelectedIds] = useState<number[]>([]);
//     const [selectAll, setSelectAll] = useState(false);
//     const [certificates, setCertificates] = useState<any[]>([]);
//     const [totalCertificates, setTotalCertificates] = useState(0);
//     const [totalPages, setTotalPages] = useState(0);
//     const [calibrated, setCalibrated] = useState(false);
//     const [loadingList, setLoadingList] = useState(false);
//     const [reloadKey, setReloadKey] = useState(0);
    
//     // Edit State
//     const [editingCertId, setEditingCertId] = useState<number | null>(null);
//     const [editingCertData, setEditingCertData] = useState<any | null>(null);
//     const [initialIssuanceDate, setInitialIssuanceDate] = useState('');

//     // Delete Confirmation Modal States
//     const [deleteModalOpen, setDeleteModalOpen] = useState(false);
//     const [certToDelete, setCertToDelete] = useState<any | null>(null);
//     const [deleteConfirmText, setDeleteConfirmText] = useState('');

//     // Filters
//     const [statusFilter, setStatusFilter] = useState('All');
//     const [courseFilter, setCourseFilter] = useState('All');
//     const [dateFilter, setDateFilter] = useState('');

//     const fileInputRef = useRef<HTMLInputElement>(null);
//     const certificateRef = useRef<HTMLDivElement>(null);

//     const CERT_BG_URL = '/verify/certificate-bg.jpg';

//     const [formData, setFormData] = useState({
//         firstName: '',
//         lastName: '',
//         courseOfStudy: '',
//         courseSummary: '',
//         yearOfStudy: '2025',
//         issuanceDate: '',
//         certificateNumber: ''
//     });

//     const buildQrPayload = (certLike: any) => {
//         const certificate_number = String(certLike?.certId || certLike?.certificateId || '').trim();
//         const verify_url = `https://speedlinktraining.com/verify/verify/${certificate_number}`;
//         return verify_url;
//     };

//     const verifyLink = (certLike: any) => {
//         const certificate_number = String(certLike?.certId || certLike?.certificateId || '').trim();
//         return `https://speedlinktraining.com/verify/verify/${certificate_number}`;
//     }

//     const formatDate = (dateString: string): string => {
//         if (!dateString) return '';
//         const date = new Date(dateString);
//         if (isNaN(date.getTime())) return dateString;
//         const day = date.getDate();
//         const month = date.toLocaleString('default', { month: 'long' });
//         const year = date.getFullYear();
//         const getOrdinalSuffix = (d: number) => {
//             if (d > 3 && d < 21) return 'th';
//             switch (d % 10) {
//                 case 1:  return 'st';
//                 case 2:  return 'nd';
//                 case 3:  return 'rd';
//                 default: return 'th';
//             }
//         };
//         return `${day}${getOrdinalSuffix(day)} of ${month} ${year}`;
//     };

//     const filteredCertificates = certificates.filter((cert: any) => {
//         const q = (searchQuery || '').toLowerCase();
//         const name = String(cert?.name || '').toLowerCase();
//         const id = String(cert?.certId || '').toLowerCase();
//         const course = String(cert?.course || '').toLowerCase();
//         const matchesSearch = name.includes(q) || id.includes(q) || course.includes(q);
//         const statusVal = String(cert?.status || '');
//         const matchesStatus = statusFilter === 'All' || statusVal === statusFilter;
//         const matchesCourse = courseFilter === 'All' || cert?.course === courseFilter;
//         const dateStr = String(cert?.date || '');
//         const matchesDate = dateFilter === '' || dateStr.slice(0, 10) === dateFilter;
//         return matchesSearch && matchesStatus && matchesCourse && matchesDate;
//     });

//     const indexOfLastItem = currentPage * itemsPerPage; 
//     const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//     const currentItems = filteredCertificates.slice(indexOfFirstItem, indexOfLastItem);
//     const totalPagesComputed = Math.max(1, Math.ceil(filteredCertificates.length / itemsPerPage || 1));

//     useEffect(() => {
//         const loadCertificates = async () => {
//             try {
//                 setLoadingList(true);
//                 if (certificates.length === 0) {
//                     try {
//                         const cached = localStorage.getItem('certificates_cache');
//                         console.log('Loaded cached certificates:', cached);
//                         if (cached) {
//                             const items = JSON.parse(cached) || [];
//                             const mapped = items.map((c: any) => ({
//                                 id: c.id,
//                                 name: c.student_name,
//                                 course: c.course_name,
//                                 certId: c.verification_code,
//                                 date: c.issued_at,
//                                 verifyUrl: c.verification_url || c.verify_url || undefined,
//                                 qrImageUrl: c.qr_url || c.qr_code_url || undefined,
//                                 courseSummary: c.course_summary || '',
//                                 status: (c.status || c.verification_status || c.state || '').toString(),
//                             }));
//                             if (mapped.length > 0) setCertificates(mapped);
//                         }
//                     } catch {}
//                 }
//                 const data = await fetchCertificates();
//                 const mapped = (data.certificates || []).map((c: any) => ({
//                     id: c.id,
//                     name: c.student_name,
//                     course: c.course_name,
//                     certId: c.verification_code,
//                     date: c.issued_at,
//                     verifyUrl: c.verification_url || c.verify_url || undefined,
//                     qrImageUrl: c.qr_url || c.qr_code_url || undefined,
//                     courseSummary: c.course_summary || '',
//                     status: (c.status || c.verification_status || c.state || '').toString(),
//                 }));
//                 setCertificates(mapped);
//                 setTotalCertificates(mapped.length);
//                 try { localStorage.setItem('certificates_cache', JSON.stringify(data.certificates)); } catch {}
//             } catch (err) {
//                 console.error(err);
//                 setTotalPages(totalPages || 10);
//             }
//             finally {
//                 setLoadingList(false);
//             }
//         };
//         loadCertificates();
//     }, []);

//     const getFitSize = (t: string, base: number, min: number, max: number) => {
//         const W = 606;
//         const canvas = document.createElement('canvas');
//         const ctx = canvas.getContext('2d');
//         let s = base;
//         if (!ctx) return Math.min(Math.max(s, min), max);
//         while (s > min) {
//             ctx.font = `${s}px 'Times New Roman', Times, serif`;
//             if (ctx.measureText(t || '').width <= W) break;
//             s -= 1;
//         }
//         if (s < min) s = min;
//         if (s > max) s = max;
//         return s;
//     };
    
//     const getFitSizeNarrow = (t: string, base: number, min: number, max: number) => {
//         const W = 260;
//         const canvas = document.createElement('canvas');
//         const ctx = canvas.getContext('2d');
//         let s = base;
//         if (!ctx) return Math.min(Math.max(s, min), max);
//         while (s > min) {
//             ctx.font = `${s}px 'Courier New', monospace`;
//             if (ctx.measureText(t || '').width <= W) break;
//             s -= 1;
//         }
//         if (s < min) s = min;
//         if (s > max) s = max;
//         return s;
//     };

//     const handlePageChange = (newPage: number) => {
//         if (newPage >= 1 && newPage <= totalPagesComputed) {
//             setCurrentPage(newPage);
//         }
//     };

//     const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
//         const { name, value } = e.target;
//         setFormData(prev => ({ ...prev, [name]: value }));
//     };

//     const handleSaveCertificate = async () => {
//         console.log("=== SAVE CERTIFICATE STARTED ===");
//         console.log("Current form data:", formData);
//         console.log("Editing cert ID:", editingCertId);
        
//         // Validation
//         if (!formData.firstName || !formData.lastName || !formData.courseOfStudy) {
//             toast.error("Please fill in the required fields: First Name, Last Name, and Course of Study.");
//             return;
//         }

//         const isEditing = !!editingCertId;
//         console.log("Is editing mode:", isEditing);
        
//         // Prepare payload
//         const payload: any = {
//             first_name: formData.firstName.trim(),
//             last_name: formData.lastName.trim(),
//             student_name: `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim(),
//             course_name: formData.courseOfStudy.trim(),
//             course_summary: formData.courseSummary.trim(),
//             year_of_study: formData.yearOfStudy,
//         };

//         // Add issuance date if provided (only send ISO yyyy-MM-dd). If the
//         // provided value can't be parsed into ISO, omit the fields to avoid
//         // backend parsing errors that cause 500 responses.
//         if (formData.issuanceDate) {
//             const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
//             if (isoDateRegex.test(formData.issuanceDate)) {
//                 payload.issuance_date = formData.issuanceDate;
//                 payload.issued_at = formData.issuanceDate;
//             } else {
//                 const parsed = Date.parse(formData.issuanceDate);
//                 if (!isNaN(parsed)) {
//                     const iso = new Date(parsed).toISOString().slice(0, 10);
//                     payload.issuance_date = iso;
//                     payload.issued_at = iso;
//                 } else {
//                     console.warn('Omitting malformed issuanceDate to avoid server error:', formData.issuanceDate);
//                 }
//             }
//         }

//         // Add certificate number if provided
//         if (formData.certificateNumber && formData.certificateNumber.trim()) {
//             const code = formData.certificateNumber.trim();
//             payload.verification_code = code;
//             payload.certificate_code = code;
//         }

//         // For editing: include original image_url and pdf_url from existing cert
//         if (isEditing && editingCertData) {
//             payload.image_url = editingCertData.image_url || "";
//             payload.pdf_url = editingCertData.pdf_url || "";
//             payload.is_active = editingCertData.is_active !== undefined ? editingCertData.is_active : true;
//         }

//         console.log("Final payload:", payload);

//         try {
//             if (isEditing) {
//                 // ===== EDITING EXISTING CERTIFICATE =====
//                 console.log("🟡 Calling editCertificate API...");
                
//                 const saved = await editCertificate(formData.certificateNumber || editingCertData?.certId, payload);
//                 console.log("✅ Edit API returned:", saved);

//                 // Update local state immediately
//                 setCertificates(prev => prev.map(c => {
//                     if (c.id !== editingCertId) return c;
                    
//                     const newName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();
//                     const newCourse = formData.courseOfStudy.trim();
//                     const newCertId = saved?.verification_code || formData.certificateNumber?.trim() || c.certId;
//                     const newDate = saved?.issued_at || formData.issuanceDate || c.date;
//                     const newSummary = saved?.course_summary || formData.courseSummary?.trim() || c.courseSummary;
                    
//                     console.log("Updating certificate locally:", {
//                         id: c.id,
//                         oldName: c.name,
//                         newName,
//                         oldCourse: c.course,
//                         newCourse,
//                         oldCertId: c.certId,
//                         newCertId,
//                         oldDate: c.date,
//                         newDate
//                     });
                    
//                     return {
//                         ...c,
//                         name: newName,
//                         course: newCourse,
//                         certId: newCertId,
//                         date: newDate,
//                         courseSummary: newSummary,
//                         status: c.status || "Verified",
//                     };
//                 }));

//                 toast.success("Certificate updated successfully!");

//                 // Refresh data from server in background
//                 setTimeout(async () => {
//                     try {
//                         console.log("🔄 Refreshing certificates from server...");
//                         const data = await fetchCertificates();
//                         const mapped = (data.certificates || []).map((c: any) => ({
//                             id: c.id,
//                             name: c.student_name,
//                             course: c.course_name,
//                             certId: c.verification_code,
//                             date: c.issued_at,
//                             verifyUrl: c.verification_url || c.verify_url || undefined,
//                             qrImageUrl: c.qr_url || c.qr_code_url || undefined,
//                             courseSummary: c.course_summary || '',
//                             status: (c.status || c.verification_status || c.state || '').toString(),
//                         }));
//                         setCertificates(mapped);
//                         setTotalCertificates(mapped.length);
//                         localStorage.setItem('certificates_cache', JSON.stringify(data.certificates));
//                         console.log("✅ Certificates refreshed successfully");
//                     } catch (err) {
//                         console.error("❌ Background refresh failed:", err);
//                     }
//                 }, 1000);

//             } else {
//                 // ===== CREATING NEW CERTIFICATE =====
//                 console.log("🟢 Calling issueCertificate API...");
                
//                 const saved = await issueCertificate(payload);
//                 console.log("✅ Create API returned:", saved);

//                 const created = {
//                     id: saved.id,
//                     name: saved.student_name,
//                     course: saved.course_name,
//                     certId: saved.verification_code,
//                     date: saved.issued_at,
//                     courseSummary: saved.course_summary || '',
//                     status: "Verified",
//                 };
                
//                 setCertificates(prev => [created, ...prev]);
//                 toast.success("Certificate issued successfully!");
//             }

//             // Close modal and reset form
//             console.log("🔵 Closing modal and resetting form...");
//             setIsModalOpen(false);
//             setEditingCertId(null);
//             setEditingCertData(null);
//             setInitialIssuanceDate('');
//             setActionMenuOpen(null);
//             setViewCert(null);
//             setFormData({
//                 firstName: "",
//                 lastName: "",
//                 courseOfStudy: "",
//                 courseSummary: "",
//                 yearOfStudy: "2025",
//                 issuanceDate: "",
//                 certificateNumber: ""
//             });
            
//             console.log("=== SAVE CERTIFICATE COMPLETED ===");

//         } catch (err: any) {
//             console.error("❌❌❌ Error saving certificate:", err);
//             console.error("Error details:", {
//                 message: err?.message,
//                 response: err?.response?.data,
//                 status: err?.response?.status
//             });
            
//             const errorMessage = err?.response?.data?.message 
//                 || err?.message 
//                 || "An error occurred while saving the certificate.";
            
//             toast.error(errorMessage);
//         }
//     };

//     const openDeleteModal = (cert: any, e: React.MouseEvent) => {
//         e.stopPropagation();
//         setCertToDelete(cert);
//         setDeleteConfirmText('');
//         setDeleteModalOpen(true);
//         setActionMenuOpen(null);
//     };

//     const confirmAndDelete = async () => {
//         if (!certToDelete || deleteConfirmText.toLowerCase() !== 'delete') return;
//         try {
//             await deleteCertificate(certToDelete.id);
//             setCertificates(prev => prev.filter(c => c.id !== certToDelete.id));
//             toast.success("Certificate deleted successfully.");
//         } catch (err: any) {
//             console.error("Delete error:", err);
//             toast.error(err?.message || "Failed to delete certificate");
//         } finally {
//             setDeleteModalOpen(false);
//             setCertToDelete(null);
//             setDeleteConfirmText('');
//         }
//     };

//     const generatePDF = async (cert: any) => {
//         setIsGeneratingPdf(true);
//         try {
//             const loadImageAsDataUrl = (url: string, mime: string = 'image/png') => new Promise<string>((resolve, reject) => {
//                 const img = new Image();
//                 img.crossOrigin = 'anonymous';
//                 img.onload = () => {
//                     try {
//                         const c = document.createElement('canvas');
//                         c.width = img.naturalWidth;
//                         c.height = img.naturalHeight;
//                         const ctx = c.getContext('2d');
//                         if (!ctx) return reject(new Error('Canvas context unavailable'));
//                         ctx.drawImage(img, 0, 0);
//                         const data = c.toDataURL(mime);
//                         resolve(data);
//                     } catch (err) {
//                         reject(err);
//                     }
//                 };
//                 img.onerror = (e) => reject(e);
//                 img.src = url;
//             });

//             let bg: string;
//             try {
//                 bg = await loadImageAsDataUrl(CERT_BG_URL, 'image/jpeg');
//             } catch (e) {
//                 console.error('Failed to load certificate background image via image loader', e);
//                 try {
//                     const r = await fetch(CERT_BG_URL);
//                     const b = await r.blob();
//                     bg = await new Promise<string>((resolve) => { const fr = new FileReader(); fr.onloadend = () => resolve(fr.result as string); fr.readAsDataURL(b); });
//                 } catch (e2) {
//                     console.warn('Fallback fetch also failed for background image', e2);
//                     if (certificateRef?.current) {
//                         try {
//                             const canvas = await html2canvas(certificateRef.current as HTMLElement, { scale: 2 });
//                             bg = canvas.toDataURL('image/jpeg', 1.0);
//                         } catch (e3) {
//                             console.error('html2canvas fallback also failed', e3);
//                             throw new Error('Failed to load certificate background image');
//                         }
//                     } else {
//                         throw new Error('Failed to load certificate background image');
//                     }
//                 }
//             }
//             const img = new Image();
//             img.src = bg;
//             await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = (e) => rej(e); });
//             const { naturalWidth: w, naturalHeight: h } = img;
//             const k = Math.min(w / 842, h / 595);
//             const px = (n: number) => Math.round(n * k);
//             const cx = (n: number) => Math.round((n * w) / 842);
//             const cy = (n: number) => Math.round((n * h) / 595);

//             const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [w, h] });
//             pdf.addImage(bg, 'JPEG', 0, 0, w, h, undefined, 'NONE');
//             pdf.setFont('times', 'bold');
//             let sName = px(50);
//             const maxW = cx(606);
//             while (sName > px(22)) { pdf.setFontSize(sName); if (pdf.getTextWidth(cert.name || '') <= maxW) break; sName -= 1; }
//             pdf.setFontSize(sName);
//             pdf.setTextColor('#111827');
//             pdf.text(cert.name, w / 2, cy(266), { align: 'center' });
//             pdf.setFont('times', 'normal');
//             let sCourse = px(30);
//             pdf.setFontSize(sCourse);
//             pdf.setTextColor('#1e3a8a');
//             const courseText = (cert.course || '').toUpperCase();
//             let courseLines = pdf.splitTextToSize(courseText, maxW);
//             if (courseLines.length > 2) courseLines = courseLines.slice(0, 2);
//             if (courseLines.length > 1) { sCourse = px(26); pdf.setFontSize(sCourse); }
//             const baseY = cy(346);
//             const gap = px(26);
//             const startY = baseY - Math.round(gap * (courseLines.length - 1) / 2);
//             courseLines.forEach((ln: string, i: number) => {
//                 pdf.text(ln, Math.round(w / 2), startY + i * gap, { align: 'center' });
//             });
//             pdf.setFont('times', 'bold');
//             pdf.setFontSize(px(16));
//             pdf.setTextColor('#111827');
//             pdf.text(formatDate(cert.date), cx(360), cy(405));
//             pdf.setFont('times', 'normal');
//             pdf.setFontSize(px(10));
//             pdf.setTextColor('#111827e9');
//             pdf.text(verifyLink(cert), cx(348), cy(532));
//             pdf.setFont('courier', 'normal');
//             const sIdBase = cert.certId === 'SHSL/25B/CS/CEH/0034' ? px(15) : px(17);
//             const sIdMin = cert.certId === 'SHSL/25B/CS/CEH/0034' ? px(9) : px(10);
//             let sId = sIdBase;
//             const maxIdW = cx(260);
//             while (sId > sIdMin) { pdf.setFontSize(sId); if (pdf.getTextWidth(cert.certId || '') <= maxIdW) break; sId -= 1; }
//             pdf.setFontSize(sId);
//             pdf.setTextColor('#6b7280');
//             pdf.text(cert.certId, w - cx(50), cy(90), { align: 'right' });
//             const qrPayload = buildQrPayload(cert);
//             let qrData: string | null = null;
//             try {
//                 qrData = await QRCode.toDataURL(qrPayload, {
//                     margin: 0,
//                     errorCorrectionLevel: 'H',
//                 });
//             } catch (e) {
//                 console.warn('QR generation failed, skipping QR:', e);
//                 qrData = null;
//             }
//             if (qrData) {
//                 try {
//                     pdf.addImage(qrData, 'PNG', w - cx(450), cy(459), cx(60), cy(55));
//                 } catch (e) {
//                     console.warn('Failed to add QR image to PDF, skipping QR:', e);
//                 }
//             }
//             pdf.save(`Certificate-${cert.certId}.pdf`);
//         } catch (err) {
//             console.error('PDF Gen Error', err);
//             const msg = err instanceof Error ? err.message : String(err);
//             toast.error(`Failed to generate PDF: ${msg}`);
//         } finally {
//             setIsGeneratingPdf(false);
//         }
//     };

//     const generatePDFForCert = async (cert: any) => {
//         setViewCert(cert);
//         await new Promise(r => setTimeout(r, 300));
//         await generatePDF(cert);
//     };

//     const handleToggleAll = (checked: boolean) => {
//         setSelectAll(checked);
//         setSelectedIds(checked ? filteredCertificates.map(c => c.id) : []);
//     };

//     const handleToggleOne = (id: number, checked: boolean) => {
//         setSelectedIds(prev => checked ? [...prev, id] : prev.filter(x => x !== id));
//     };

//     const handleBulkDownload = async () => {
//         const items = selectedIds.length > 0
//             ? certificates.filter(c => selectedIds.includes(c.id))
//             : filteredCertificates;
//         for (const item of items) {
//             await generatePDFForCert(item);
//         }
//         setViewCert(null);
//     };

//     const handleImportClick = () => fileInputRef.current?.click();

//     const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
//         const file = e.target.files?.[0];
//         if (!file) return;
//         const allowed = [".csv", ".xlsx", ".xls"];
//         const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
//         if (!allowed.includes(ext)) {
//             toast.error("Please upload a CSV or Excel file.");
//             e.target.value = "";
//             return;
//         }
//         try {
//             toast.info("Uploading...");
//             await importCertificatesCsv(file);
//             toast.success("Imported successfully!");
//             const data = await fetchCertificates();
//             const mapped = (data.certificates || []).map((c: any) => ({
//                 id: c.id,
//                 name: c.student_name,
//                 course: c.course_name,
//                 certId: c.verification_code,
//                 date: c.issued_at,
//                 status: "Verified"
//             }));
//             setCertificates(mapped);
//             setTotalCertificates(mapped.length);
//         } catch (err: any) {
//             toast.error(err?.message || "Import failed");
//         } finally {
//             e.target.value = "";
//         }
//     };

//     const handleExport = () => {
//         const headers = ["Name", "Course", "Certificate ID", "Date", "Status"];
//         const rows = certificates.map(c => [c.name, c.course, c.certId, c.date, c.status]);
//         const csv = "data:text/csv;charset=utf-8," + headers.join(",") + "\n" + rows.map(r => `"${r.join('","')}"`).join("\n");
//         const link = document.createElement("a");
//         link.setAttribute("href", encodeURI(csv));
//         link.setAttribute("download", "certificates.csv");
//         document.body.appendChild(link);
//         link.click();
//         document.body.removeChild(link);
//     };

//     const toggleActionMenu = (id: number) => {
//         setActionMenuOpen(prev => prev === id ? null : id);
//     };

//     useEffect(() => {
//         const handleClickOutside = (e: MouseEvent) => {
//             if (!(e.target as Element).closest('.action-menu-container')) {
//                 setActionMenuOpen(null);
//             }
//         };
//         document.addEventListener('mousedown', handleClickOutside);
//         return () => document.removeEventListener('mousedown', handleClickOutside);
//     }, []);

//     const handleEditClick = (cert: any) => {
//         const [first, ...last] = cert.name.split(' ');
//         const normalizedDate = (() => {
//             const v = cert.date;
//             if (!v) return '';
//             const isoRegex = /^\d{4}-\d{2}-\d{2}$/;
//             if (typeof v === 'string') {
//                 if (isoRegex.test(v)) return v.slice(0, 10);
//                 const parsed = Date.parse(v);
//                 if (!isNaN(parsed)) return new Date(parsed).toISOString().slice(0, 10);
//                 return '';
//             }
//             if (v instanceof Date) {
//                 const t = new Date(v.getTime() - v.getTimezoneOffset() * 60000);
//                 return t.toISOString().slice(0, 10);
//             }
//             return '';
//         })();
//         setFormData({
//             firstName: first || '',
//             lastName: last.join(' '),
//             courseOfStudy: cert.course,
//             courseSummary: cert.courseSummary || '',
//             yearOfStudy: '2025',
//             issuanceDate: normalizedDate,
//             certificateNumber: cert.certId
//         });
//         setInitialIssuanceDate(normalizedDate);
//         setEditingCertId(cert.id);
//         setEditingCertData(cert);
//         setIsModalOpen(true);
//         setActionMenuOpen(null);
//     };

//     const handleOpenIssueModal = () => {
//         setFormData({
//             firstName: '', lastName: '', courseOfStudy: '', courseSummary: '',
//             yearOfStudy: '2025', issuanceDate: '', certificateNumber: ''
//         });
//         setEditingCertId(null);
//         setEditingCertData(null);
//         setInitialIssuanceDate('');
//         setIsModalOpen(true);
//     };

//     return (
//         <div className="space-y-6">
//             <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".csv,.xlsx,.xls" className="hidden" />

//             {/* Header */}
//             <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
//                 <div>
//                     <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Certificates</h2>
//                     <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mt-1">Manage and verify issued certificates.</p>
//                 </div>
//                 <div className="flex flex-col sm:flex-row gap-3">
//                     <div className="relative">
//                         <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
//                         <input 
//                             type="text" 
//                             placeholder="Search certificates..." 
//                             value={searchQuery}
//                             onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
//                             className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm w-full md:w-64 focus:ring-primary focus:border-primary bg-white dark:bg-gray-700"
//                         />
//                     </div>
//                     <button onClick={handleImportClick} className="bg-white dark:bg-gray-800 border border-primary text-primary px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-50 dark:hover:bg-gray-700 flex items-center gap-2">
//                         <FileUp className="w-4 h-4" /> Import CSV
//                     </button>
//                     <button onClick={handleOpenIssueModal} className="bg-primary hover:bg-indigo-800 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
//                         <Plus className="w-4 h-4" /> Issue Certificate
//                     </button>
//                 </div>
//             </div>

//             {/* Table */}
//             <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border overflow-hidden">
//                 <div className="p-4 border-b flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
//                     <div className="flex items-center gap-2">
//                         <button onClick={handleExport} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-md text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600">
//                             <FileDown className="w-4 h-4" /> Export
//                         </button>
//                         <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-3 py-1.5 border rounded-md text-sm font-medium ${showFilters ? 'bg-primary text-white border-primary' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
//                             <Filter className="w-3 h-3" /> Filters
//                         </button>
//                     </div>
//                     <div className="flex items-center gap-2">
//                         <button
//                             onClick={handleBulkDownload}
//                             disabled={selectedIds.length === 0}
//                             className={`px-3 py-1.5 border rounded-md text-xs font-semibold ${
//                                 selectedIds.length > 0
//                                     ? 'border-primary text-white bg-primary hover:bg-indigo-900 dark:hover:bg-gray-700'
//                                     : 'border-gray-300 text-gray-400 cursor-not-allowed bg-gray-100 dark:bg-gray-700'
//                             }`}
//                         >
//                             Download Selected
//                         </button>
//                     </div>
//                 </div>

//                 {showFilters && (
//                     <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border-b grid grid-cols-1 sm:grid-cols-3 gap-4">
//                         <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} className="border rounded-md p-2 text-sm">
//                             <option value="All">Status: All</option>
//                             <option value="Verified">Verified</option>
//                             <option value="Revoked">Revoked</option>
//                         </select>
//                         <select value={courseFilter} onChange={(e) => { setCourseFilter(e.target.value); setCurrentPage(1); }} className="border rounded-md p-2 text-sm">
//                             <option value="All">Course: All</option>
//                             <option value="Full Stack Web Dev">Full Stack Web Dev</option>
//                             <option value="Data Analytics">Data Analytics</option>
//                             <option value="Cybersecurity">Cybersecurity</option>
//                         </select>
//                         <input type="date" value={dateFilter} onChange={(e) => { setDateFilter(e.target.value); setCurrentPage(1); }} className="border rounded-md p-2 text-sm" />
//                     </div>
//                 )}

//                 <div className="overflow-x-auto">
//                     <table className="w-full text-left text-sm text-gray-700 dark:text-gray-300">
//                         <thead className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs uppercase font-bold">
//                             <tr>
//                                 <th className="px-6 py-3"><input type="checkbox" checked={selectAll} onChange={(e) => handleToggleAll(e.target.checked)} className="rounded" /></th>
//                                 <th className="px-6 py-3">Name</th>
//                                 <th className="px-6 py-3">Course</th>
//                                 <th className="px-6 py-3">Certificate ID</th>
//                                 <th className="px-6 py-3">Date</th>
//                                 <th className="px-6 py-3">Action</th>
//                             </tr>
//                         </thead>
//                         <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
//                             {currentItems.length > 0 ? currentItems.map((cert, idx) => (
//                                 <tr key={`${cert?.id ?? cert?.certId ?? cert?.name ?? 'row'}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
//                                     <td className="px-6 py-4"><input type="checkbox" checked={selectedIds.includes(cert.id)} onChange={(e) => handleToggleOne(cert.id, e.target.checked)} className="rounded" /></td>
//                                     <td className="px-6 py-4 font-semibold text-gray-900 dark:text-white">{cert.name}</td>
//                                     <td className="px-6 py-4">{cert.course}</td>
//                                     <td className="px-6 py-4 font-mono text-xs">{cert.certId}</td>
//                                     <td className="px-6 py-4">{cert.date}</td>
//                                     <td className="px-6 py-4 action-menu-container relative">
//                                         <button onClick={(e) => { e.stopPropagation(); toggleActionMenu(cert.id); }} className="p-1">
//                                             <MoreHorizontal className="w-5 h-5" />
//                                         </button>
//                                         {actionMenuOpen === cert.id && (
//                                             <div className="absolute right-8 top-0 mt-2 w-32 bg-white dark:bg-gray-800 rounded-md shadow-lg border z-50">
//                                                 <div className="py-1">
//                                                     <button onClick={() => { setViewCert(cert); setActionMenuOpen(null); }} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left font-medium">
//                                                         <Eye className="w-4 h-4" /> View
//                                                     </button>
//                                                     <button onClick={() => handleEditClick(cert)} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left font-medium">
//                                                         <Edit className="w-4 h-4" /> Edit
//                                                     </button>
//                                                     <button onClick={(e) => openDeleteModal(cert, e)} className="flex items-center gap-2 px-4 py-2 text-sm text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 w-full text-left font-medium">
//                                                         <Trash className="w-4 h-4" /> Delete
//                                                     </button> 
//                                                 </div>
//                                             </div>
//                                         )}
//                                     </td>
//                                 </tr>
//                             )) : (
//                                 <tr><td colSpan={6} className="text-center py-8 text-gray-500">No certificates found</td></tr>
//                             )}
//                         </tbody>
//                     </table>
//                 </div>

//                 <div className="p-4 border-t flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-800/50">
//                     <span>
//                         {filteredCertificates.length === 0
//                             ? 'Showing 0-0 of 0'
//                             : `Showing ${indexOfFirstItem + 1}-${Math.min(indexOfLastItem, filteredCertificates.length)} of ${filteredCertificates.length}`}
//                     </span>
//                     <div className="flex gap-2">
//                         <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded disabled:opacity-50">Previous</button>
//                         {Array.from({ length: totalPagesComputed }, (_, i) => i + 1).map(p => (
//                             <button key={p} onClick={() => handlePageChange(p)} className={`px-3 py-1 rounded ${currentPage === p ? 'bg-primary text-white' : 'border'}`}>{p}</button>
//                         ))}
//                         <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPagesComputed} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
//                     </div>
//                 </div>
//             </div>

//             {/* DELETE CONFIRMATION MODAL */}
//             {deleteModalOpen && (
//                 <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
//                     <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6 border relative">
//                         <button
//                             onClick={() => {
//                                 setDeleteModalOpen(false);
//                                 setCertToDelete(null);
//                                 setDeleteConfirmText('');
//                             }}
//                             className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
//                         >
//                             <X className="w-6 h-6" />
//                         </button>

//                         <div className="flex items-center gap-3 text-red-600 mb-4">
//                             <AlertCircle className="w-8 h-8" />
//                             <h3 className="text-xl font-bold">Delete Certificate?</h3>
//                         </div>

//                         <p className="text-gray-700 dark:text-gray-300 mb-6">
//                             Are you sure you want to delete the certificate for <strong>{certToDelete?.name}</strong>?<br />
//                             <span className="font-bold text-red-600">This action cannot be undone.</span>
//                         </p>

//                         <div className="mb-6">
//                             <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
//                                 Type <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">delete</span> to confirm
//                             </label>
//                             <input
//                                 type="text"
//                                 value={deleteConfirmText}
//                                 onChange={(e) => setDeleteConfirmText(e.target.value)}
//                                 className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 text-gray-900 dark:text-white"
//                                 placeholder="delete"
//                                 autoFocus
//                             />
//                         </div>

//                         <div className="flex justify-end gap-3">
//                             <button
//                                 onClick={() => {
//                                     setDeleteModalOpen(false);
//                                     setCertToDelete(null);
//                                     setDeleteConfirmText('');
//                                 }}
//                                 className="px-5 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 font-medium transition-colors"
//                             >
//                                 Cancel
//                             </button>
//                             <button
//                                 onClick={confirmAndDelete}
//                                 disabled={deleteConfirmText.toLowerCase() !== 'delete'}
//                                 className="px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
//                             >
//                                 <Trash className="w-4 h-4" /> Proceed & Delete
//                             </button>
//                         </div>
//                     </div>
//                 </div>
//             )}

//             {/* View Certificate Modal */}
//             {viewCert && (
//                 <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
//                     <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-5xl shadow-2xl p-6 border max-h-[95vh] overflow-auto">
//                         <div className="flex justify-between items-center mb-4 pb-2 border-b">
//                             <h3 className="text-lg font-bold flex items-center gap-2"><Award className="w-5 h-5 text-yellow-600" /> Certificate Preview</h3>
//                             <div className="flex gap-3">
//                                 <button onClick={() => generatePDF(viewCert)} disabled={isGeneratingPdf} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-800 flex items-center gap-2 disabled:opacity-50">
//                                     {isGeneratingPdf ? 'Generating...' : <><Download className="w-4 h-4" /> Download PDF</>}
//                                 </button>
//                                 <button onClick={() => setViewCert(null)}><X className="w-6 h-6" /></button>
//                             </div>
//                         </div>
//                         <div className="flex-grow overflow-auto bg-gray-200 dark:bg-gray-900 p-8 rounded-lg flex items-center justify-center">
//                             <div ref={certificateRef} className="w-[842px] h-[595px] relative shadow-2xl bg-white" style={{ backgroundImage: `url(${CERT_BG_URL})`, backgroundSize: '100% 100%' }}>
//                                 <div className="relative z-10 w-full h-full text-center px-0">
//                                     <h2 className="font-serif font-bold text-gray-900 capitalize absolute left-1/2 -translate-x-1/2" style={{ top: '235px', fontSize: `${getFitSize(viewCert.name, 32, 20, 30)}px` }}>
//                                         {viewCert.name}
//                                     </h2>
//                                     <h3 className="font-medium text-indigo-900 uppercase absolute left-1/2 -translate-x-1/2 max-w-[92%] text-center whitespace-normal break-words" style={{ top: '305px', fontSize: `${getFitSize(viewCert.course, 26, 16, 20)}px` }}>
//                                         {viewCert.course}
//                                     </h3>
//                                     <div className="absolute" style={{ top: '395px', left: '340px', fontSize: '14px' }}>
//                                         <p className=" text-gray-800 font-bold">{formatDate(viewCert.date)}</p>
//                                     </div>
//                                     <div className="absolute" style={{ top: '522px', left: '349px', fontSize: '10px' }}>
//                                         <p className="text-gray-800 italic">{verifyLink(viewCert)}</p>
//                                     </div>
//                                     <div className="absolute" style={{ top: '73px', right: '49px' }}>
//                                         <span className="font-mono text-gray-800" style={{ fontSize: `${viewCert.certId === 'SHSL/25B/CS/CEH/0034' ? getFitSizeNarrow(viewCert.certId, 12, 9, 14) : getFitSizeNarrow(viewCert.certId, 14, 10, 16)}px` }}>{viewCert.certId}</span>
//                                     </div>
//                                     <div className="absolute" style={{ top: '435px', right: '383px', width: '85px', height: '60px' }}>
//                                         <QRCodeCanvas
//                                             value={buildQrPayload(viewCert)}
//                                             size={65}
//                                             level="H"
//                                             includeMargin={false}
//                                         />
//                                     </div>
//                                 </div>
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//             )}

//             {/* Issue/Edit Modal */}
//             {isModalOpen && (
//                 <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
//                     <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto border">
//                         <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 z-10">
//                             <h3 className="text-xl font-bold text-gray-900 dark:text-white">{editingCertId ? 'Update Certificate' : 'Issue Certificate'}</h3>
//                             <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"><X className="w-5 h-5" /></button>
//                         </div>
//                         <div className="p-6 space-y-8">
//                             {!editingCertId && (
//                                 <div className="bg-gray-50 dark:bg-gray-700/50 p-6 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
//                                     <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-2">To issue bulk certificates you can import the database below</p>
//                                     <a href="#" className="text-xs text-primary hover:underline mb-4 block font-semibold">Download Sample CSV File</a>
//                                     <button onClick={handleImportClick} className="flex items-center gap-2 px-4 py-2 border border-primary text-primary dark:text-indigo-400 bg-white dark:bg-gray-800 rounded-md text-sm font-semibold hover:bg-blue-50 dark:hover:bg-gray-700 shadow-sm">
//                                         <FileUp className="w-4 h-4" /> Import CSV File
//                                     </button>
//                                 </div>
//                             )}

//                             <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Input Student Details</h4>
//                             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                                 {['First Name', 'Last Name', 'Course of Study', 'Course Summary', 'Year of Study', 'Issuance Date', 'Certificate Number'].map(label => {
//                                     const name = label.toLowerCase().replace(/\s/g, '').replace('ofstudy', 'OfStudy').replace('firstname', 'firstName').replace('lastname', 'lastName').replace('coursesummary', 'courseSummary').replace('yearofstudy', 'yearOfStudy').replace('issuancedate', 'issuanceDate').replace('certificatenumber', 'certificateNumber');
//                                     return (
//                                         <div key={label}>
//                                             <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">{label} {label !== 'Certificate Number' && '*'}</label>
//                                             <input 
//                                                 type={label.includes('Date') ? 'date' : 'text'}
//                                                 name={name} 
//                                                 value={(formData as any)[name]} 
//                                                 onChange={handleInputChange} 
//                                                 required={label !== 'Certificate Number' && !editingCertId}
//                                                 className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 focus:ring-primary focus:border-primary text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700" 
//                                                 placeholder={`Enter ${label}`} 
//                                             />
//                                         </div>
//                                     );
//                                 })}
//                             </div>
//                             <div className="mt-8">
//                                 <button onClick={handleSaveCertificate} className="w-fit bg-primary text-white px-8 py-3 rounded-lg text-sm font-bold hover:bg-indigo-800 shadow-md flex items-center gap-2">
//                                     {editingCertId ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
//                                     {editingCertId ? 'Update Certificate' : 'Issue Certificate'}
//                                 </button>
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//             )}
//         </div>
//     );
// };

// export default Certificates;